import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Loader2, AlertCircle } from 'lucide-react';

const AddressAutocompleteInput = ({ 
  value = '', 
  onChange, 
  onSelect, 
  placeholder = 'Enter your home address',
  className = '',
  inputClassName = '',
  dropdownClassName = '',
  showIcon = true,
  autoFocus = false,
  disabled = false
}) => {
  const [addressQuery, setAddressQuery] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  
  const debounceTimerRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  
  const MAPBOX_TOKEN = import.meta.env?.VITE_MAPBOX_ACCESS_TOKEN;

  // Sync external value changes
  useEffect(() => {
    setAddressQuery(value);
  }, [value]);

  // Fetch Mapbox autocomplete suggestions (exact same logic as wizard)
  const fetchSuggestions = async (query) => {
    if (!query || query?.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsSuggesting(true);
    setSuggestError(null);

    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&autoComplete=true&types=address&country=US&limit=5`;
      const response = await fetch(url);
      const data = await response?.json();

      if (data?.features && data?.features?.length > 0) {
        setSuggestions(data?.features);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Mapbox autocomplete error:', error);
      setSuggestError("Couldn't load suggestions");
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsSuggesting(false);
    }
  };

  // Debounced input change handler (300ms delay - same as wizard)
  useEffect(() => {
    if (debounceTimerRef?.current) {
      clearTimeout(debounceTimerRef?.current);
    }

    if (addressQuery?.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchSuggestions(addressQuery);
    }, 300);

    return () => {
      if (debounceTimerRef?.current) {
        clearTimeout(debounceTimerRef?.current);
      }
    };
  }, [addressQuery]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target) && 
          inputRef?.current && !inputRef?.current?.contains(event?.target)) {
        setShowSuggestions(false);
        setActiveIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddressChange = (e) => {
    const newValue = e?.target?.value;
    setAddressQuery(newValue);
    setActiveIndex(-1);
    
    // Notify parent of raw input change
    if (onChange) {
      onChange(newValue);
    }
  };

  // Handle suggestion selection (exact same logic as wizard)
  const handleSelectSuggestion = (feature) => {
    const placeName = feature?.place_name;
    const lng = feature?.center?.[0];
    const lat = feature?.center?.[1];

    setAddressQuery(placeName);
    setShowSuggestions(false);
    setActiveIndex(-1);
    
    // Notify parent with full address data
    if (onSelect) {
      onSelect({
        address: placeName,
        fullAddress: placeName,
        lng,
        lat,
        wizardAddress: placeName,
        selected: true
      });
    }
  };

  // Keyboard navigation (exact same as wizard)
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions?.length === 0) {
      if (e?.key === 'Enter' && addressQuery?.trim()) {
        // Allow parent to handle enter on typed address
        e?.preventDefault();
      }
      return;
    }

    switch (e?.key) {
      case 'ArrowDown':
        e?.preventDefault();
        setActiveIndex(prev => 
          prev < suggestions?.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e?.preventDefault();
        setActiveIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e?.preventDefault();
        if (activeIndex >= 0 && activeIndex < suggestions?.length) {
          handleSelectSuggestion(suggestions?.[activeIndex]);
        }
        break;
      case 'Escape':
        e?.preventDefault();
        setShowSuggestions(false);
        setActiveIndex(-1);
        break;
      default:
        break;
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Input Field */}
      <div className="relative">
        {showIcon && (
          <div className="absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 pointer-events-none z-10">
            <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-white/80" />
          </div>
        )}
        
        <input
          ref={inputRef}
          type="text"
          value={addressQuery}
          onChange={handleAddressChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={inputClassName}
          autoFocus={autoFocus}
          disabled={disabled}
          aria-label="Address autocomplete input"
          aria-autoComplete="list"
          aria-expanded={showSuggestions}
          aria-controls="address-suggestions"
        />
        
        {/* Loading Indicator */}
        {isSuggesting && (
          <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
            <Loader2 className="w-4 h-4 text-white/60 animate-spin" />
          </div>
        )}
      </div>

      {/* Dropdown Suggestions */}
      {showSuggestions && suggestions?.length > 0 && (
        <div
          ref={dropdownRef}
          id="address-suggestions"
          role="listbox"
          className={`absolute left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50 ${dropdownClassName}`}
        >
          {suggestions?.map((feature, index) => (
            <div
              key={feature?.id || index}
              role="option"
              aria-selected={activeIndex === index}
              onClick={() => handleSelectSuggestion(feature)}
              onMouseEnter={() => setActiveIndex(index)}
              className={`px-4 py-3 cursor-pointer transition-colors flex items-start gap-3 ${
                activeIndex === index
                  ? 'bg-blue-50 border-l-4 border-blue-500' :'hover:bg-gray-50 border-l-4 border-transparent'
              }`}
            >
              <MapPin className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                activeIndex === index ? 'text-blue-600' : 'text-gray-400'
              }`} />
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${
                  activeIndex === index ? 'text-blue-900' : 'text-gray-900'
                }`}>
                  {feature?.place_name}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No Results State */}
      {showSuggestions && suggestions?.length === 0 && !isSuggesting && addressQuery?.length >= 3 && (
        <div
          ref={dropdownRef}
          className={`absolute left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 p-4 z-50 ${dropdownClassName}`}
        >
          <div className="flex items-center gap-3 text-gray-500">
            <AlertCircle className="w-5 h-5" />
            <p className="text-sm">No addresses found. Try a different search.</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {suggestError && (
        <div
          ref={dropdownRef}
          className={`absolute left-0 right-0 mt-2 bg-red-50 rounded-xl shadow-2xl border border-red-200 p-4 z-50 ${dropdownClassName}`}
        >
          <div className="flex items-center gap-3 text-red-600">
            <AlertCircle className="w-5 h-5" />
            <p className="text-sm">{suggestError}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressAutocompleteInput;